import { useState } from 'react';

import {
  validateCorrection,
  type CorrectionFieldErrors,
  type CorrectionFormValues,
} from '../features/committee/application/employeeCorrection';
import { Alert, Button, Modal, SelectField, TextField } from '../shared/components';

import styles from './ScreenLayout.module.css';
import cardStyles from './EmployeeCorrectionCard.module.css';

/**
 * Correcting what the roster got wrong, in a dialog over the list.
 *
 * Every imported field is here, including the school email — the first live
 * test found a record carrying `anas.m@` for someone whose account is `anas@`,
 * and with the address unreachable there was nothing anyone could do but tell
 * them to register and wait.
 *
 * Enrolment and coverage are still absent: this fixes records, it does not
 * decide entitlements.
 */

export interface EmployeeCorrectionCardProps {
  readonly employeeNumber: string;
  readonly initialValues: CorrectionFormValues;
  readonly branches: readonly { readonly id: string; readonly nameAr: string }[];
  readonly saving: boolean;
  readonly errorAr: string | null;
  readonly onSave: (values: CorrectionFormValues) => void;
  readonly onCancel: () => void;
}

export function EmployeeCorrectionCard({
  employeeNumber,
  initialValues,
  branches,
  saving,
  errorAr,
  onSave,
  onCancel,
}: EmployeeCorrectionCardProps) {
  const [values, setValues] = useState<CorrectionFormValues>(initialValues);
  const [errors, setErrors] = useState<CorrectionFieldErrors>({});

  const set =
    (field: keyof CorrectionFormValues) => (event: { target: { value: string } }) => {
      setValues((current) => ({ ...current, [field]: event.target.value }));
    };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const result = validateCorrection(values);
    setErrors(result.errors);
    if (!result.valid) return;

    onSave(values);
  };

  return (
    <Modal
      title="تصحيح سجل موظف"
      description={`الرقم الوظيفي ${employeeNumber} — لا يتغيّر`}
      onClose={onCancel}
    >
      {errorAr ? <Alert severity="error">{errorAr}</Alert> : null}

      <Alert severity="info">
        هذا سجل الكشف. ما يدخله الموظف بنفسه في «بياناتي» لا يُعدَّل من هنا.
      </Alert>

      <form onSubmit={handleSubmit} noValidate>
        <div className={styles.formGrid}>
          <div className={styles.spanAll}>
            <TextField
              label="الاسم في الكشف"
              required
              value={values.fullName}
              onChange={set('fullName')}
              error={errors.fullName}
            />
          </div>

          <SelectField
            label="الفرع"
            required
            value={values.branchId}
            onChange={set('branchId')}
            error={errors.branchId}
          >
            <option value="" disabled>
              اختر…
            </option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.nameAr}
              </option>
            ))}
          </SelectField>

          <TextField
            label="البريد المدرسي"
            optional
            type="email"
            dir="ltr"
            value={values.workEmail}
            onChange={set('workEmail')}
            error={errors.workEmail}
            hint="به يُربط الموظف بسجله عند أول دخول"
          />

          <TextField
            label="رقم الجوال"
            optional
            numeric
            inputMode="numeric"
            placeholder="7XXXXXXXX"
            value={values.phone}
            onChange={set('phone')}
            error={errors.phone}
          />

          <SelectField
            label="الحالة الاجتماعية في الكشف"
            value={values.declaredMaritalStatus}
            onChange={set('declaredMaritalStatus')}
            error={errors.declaredMaritalStatus}
          >
            {/* Kept as a real third state: the import left it unset wherever
                the spreadsheet said something it could not read. */}
            <option value="">غير محددة</option>
            <option value="married">متزوج</option>
            <option value="single">غير متزوج</option>
          </SelectField>

          <TextField
            label="عدد الأبناء في الكشف"
            required
            numeric
            inputMode="numeric"
            value={values.declaredChildrenCount}
            onChange={set('declaredChildrenCount')}
            error={errors.declaredChildrenCount}
            hint="يُقارَن بما يُدخله الموظف من معالين"
          />

          <div className={styles.spanAll}>
            <TextField
              label="العنوان"
              optional
              value={values.address}
              onChange={set('address')}
              error={errors.address}
            />
          </div>

          <div className={styles.spanAll}>
            <div className={cardStyles.actions}>
              <Button type="submit" loading={saving}>
                حفظ التصحيح
              </Button>
              <Button type="button" variant="ghost" onClick={onCancel}>
                إلغاء
              </Button>
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
}
